/**
 * Dispenser (rules v2) view helpers, free of Phaser so they are unit-tested in node: which
 * pieces sit on the board (tiles, crates, machine items) versus on the conveyor belt, and what
 * each machine shows. Core decides every rule; these only read its compiled level and state.
 *
 * Piece order (core CompiledLevel): board tiles, crates, conveyor entries, then every
 * dispenser's items (top first). A machine is one tile-sized cell at its items' shared x, y,
 * layer; only its top item is shown, and tappable while nothing lies over the machine.
 */
import { dispenserLeft, dispenserTop, type CompiledLevel, type Round } from '@mp/core';

/** First conveyor piece (also the end of tiles + crates). */
export const convStart = (L: CompiledLevel): number => L.nBoard + L.nCrates;

/** Is piece i a conveyor queue entry (not tiles, crates or dispenser items)? */
export function isConveyorPiece(L: CompiledLevel, i: number): boolean {
  const s = convStart(L);
  return i >= s && i < s + L.nConv;
}

/**
 * Pieces with a board cell: tiles, crates, every dispenser item (a machine's items share its cell)
 * and tray pieces; never a conveyor entry or a shelf item (off the grid, drawn in the strip).
 */
export function boardPieces(L: CompiledLevel): number[] {
  const out: number[] = [];
  for (let i = 0; i < L.n; i++) if (!isConveyorPiece(L, i) && !(L.nShelves && L.shelfOf[i]! >= 0)) out.push(i);
  return out;
}

/** Dispenser index of piece i, or -1. */
export function machineOf(L: CompiledLevel, i: number): number {
  return L.nDisp ? (L.dispOf[i] ?? -1) : -1;
}

export interface MachineState {
  /** the piece shown in the window, -1 once empty */
  top: number;
  /** items still in the machine (the counter badge) */
  left: number;
  /** a tile or crate lies over the machine: dimmed like a covered tile, not tappable */
  covered: boolean;
  /** empty: the machine is gone and frees what it stood on */
  gone: boolean;
}

export function machineState(r: Round, d: number): MachineState {
  const top = dispenserTop(r, d);
  const left = dispenserLeft(r, d);
  return { top, left, covered: top >= 0 && r.cover[top]! > 0, gone: left === 0 };
}

/** Counter badge text: the items still in the machine. */
export const counterText = (left: number): string => `×${left}`;


/**
 * Machine art geometry in texture px (MachineView bakes it; the box is one tile wide, so a
 * tile edge is `w` px): the body, the item window and the striped awning on top.
 */
export const MACHINE_ART = {
  w: 200,
  h: 224,
  win: { x: 30, y: 58, w: 140, h: 116, r: 18 },
  awning: { x: 4, y: 4, w: 192, h: 40 },
} as const;

/**
 * A covered machine is not hidden: its awning and counter badge rise (in tile edges) to peek
 * out above the tile lying over it, dimmed like any covered piece, and settle back as it is
 * uncovered. A board tile face reaches 0.48 tile above its centre, plus the layer lift.
 */
export const PEEK = { awning: 0.22, badge: 0.24 } as const;

/** Device-px rise of the awning and badge at `shade` (0 covered … 1 lit) for a `tile`-px cell. */
export function peekLift(shade: number, tile: number): { awning: number; badge: number } {
  const k = Math.min(1, Math.max(0, 1 - shade));
  return { awning: k * PEEK.awning * tile, badge: k * PEEK.badge * tile };
}

/**
 * Clip for an item rising into (or sinking back out of) the window, relative to the machine's
 * centre in device px: the window's width, from the machine's top down to the window's bottom
 * edge, so nothing shows below the glass (the overshoot above the window stays visible).
 */
export function itemClip(tile: number): { x: number; y: number; w: number; h: number } {
  const A = MACHINE_ART;
  const k = tile / A.w;
  return { x: (A.win.x - A.w / 2) * k, y: -A.h / 2 * k, w: A.win.w * k, h: (A.win.y + A.win.h) * k };
}
